// DASHBOARD — the trader's home: connection, account mode and risk state, what is open right now and
// today's P&L across every environment (LIVE / SHADOW / PAPER) and every product the account trades.
// Read-only; every figure comes from the common Trading Book endpoints, never computed here.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { inr, inrPnl, pnlColor } from "@/lib/format";
import { accountMode } from "@/lib/modes";
import { useProducts } from "@/lib/products";
import {
  LayoutDashboard, PlugZap, Activity, ShieldCheck, ShieldAlert, Crosshair, Zap, ArrowRight,
  SlidersHorizontal, Layers, BookOpen, Wallet,
} from "lucide-react";
import { sharedGet, visibleInterval } from "@/lib/poll";

function Panel({ title, icon: Icon, children, testid }) {
  return (
    <div className="term-panel rounded-sm p-4" data-testid={testid}>
      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4" /> {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value, cls, testid }) {
  return (
    <div className="flex items-center justify-between text-sm py-1" data-testid={testid}>
      <span className="text-slate-500">{label}</span>
      <span className={`mono font-semibold ${cls || "text-slate-100"}`}>{value}</span>
    </div>
  );
}

function Go({ to, icon: Icon, label, primary, testid }) {
  const nav = useNavigate();
  return (
    <button onClick={() => nav(to)} data-testid={testid}
      className={`text-sm font-semibold px-4 py-2 rounded-sm flex items-center gap-2 ${primary ? "bg-[#2563EB] hover:bg-[#1d4fd0] text-white"
        : "bg-[#1A1D24] border border-[#2A2F3A] hover:bg-[#232730]"}`}>
      <Icon className="h-4 w-4" /> {label} <ArrowRight className="h-3.5 w-3.5" />
    </button>
  );
}

const ENVS = ["LIVE", "SHADOW", "PAPER"];

export default function Dashboard() {
  const products = useProducts();
  const [status, setStatus] = useState(null);
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState(null);
  const [today, setToday] = useState(null);
  const [parity, setParity] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    const load = () => {
      sharedGet("/system-status").then((r) => setStatus(r.data)).catch(() => {});
      sharedGet("/account/summary").then((r) => setAccount(r.data)).catch(() => setAccount(null));
      api.get("/paper-book/positions").then((r) => setPositions(r.data)).catch(() => {});
      api.get("/paper-book/pnl", { params: { period: "TODAY" } }).then((r) => setToday(r.data)).catch(() => {});
      if (products.futures) {
        sharedGet("/paper/v25/trades", { params: { market: "ALL", environment: "ALL" } })
          .then((r) => setParity(r.data.parity || {})).catch(() => {});
      }
    };
    load();
    return visibleInterval(load, 5000);
  }, [products.futures]);

  if (!status || !positions) return <div className="p-6 text-slate-500">Loading dashboard…</div>;

  const zConnected = status.zerodha_session === "CONNECTED";
  const marketLive = status.market_status === "LIVE";
  const rows = positions.positions || [];
  const t = positions.totals || {};
  const byEnv = Object.fromEntries(ENVS.map((e) => [e, rows.filter((r) => r.environment === e).length]));
  const risk = account?.risk || {};
  const s = today?.summary || {};
  const blocked = risk.kill_switch || risk.global_kill_switch || risk.stop_new_entries
    || (risk.recon_state && risk.recon_state !== "RECONCILED");
  const unpriced = t.unpriced_trades && t.unpriced_trades === t.open_trades;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <LayoutDashboard className="h-6 w-6 text-[#2563EB]" /> Dashboard
        </h1>
        <div className="text-[11px] mono text-slate-500" data-testid="dash-account">
          {account?.account?.broker_user_id ? `Zerodha ${account.account.broker_user_id}` : "No Zerodha account linked"}
          {account?.products ? ` · ${{ FUTURES_ONLY: "Futures", OPTIONS_ONLY: "Options", BOTH: "Futures + Options" }[account.products]}` : ""}
          {account ? ` · ${accountMode({ mode: account.futures_mode, options_mode: account.options_mode, products: account.products }).detail}` : ""}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <Panel title="Zerodha" icon={PlugZap} testid="dash-zerodha">
          <div className={`flex items-center gap-2 text-sm font-bold ${zConnected ? "text-[#00C805]" : "text-[#FF6b62]"}`}>
            <span className={`h-2.5 w-2.5 rounded-full ${zConnected ? "bg-[#00C805]" : "bg-[#FF3B30]"}`} />
            {zConnected ? "Connected" : "Disconnected"}
          </div>
          {!zConnected && (
            <button onClick={() => nav("/broker")} className="mt-3 w-full text-xs font-semibold py-1.5 rounded-sm bg-[#2563EB] text-white">Go to Broker Session</button>
          )}
        </Panel>

        <Panel title="Risk & safety" icon={blocked ? ShieldAlert : ShieldCheck} testid="dash-risk">
          <div className={`text-sm font-bold ${blocked ? "text-[#FF6b62]" : "text-[#00C805]"}`} data-testid="dash-risk-state">
            {risk.kill_switch || risk.global_kill_switch ? "KILL SWITCH ON"
              : risk.stop_new_entries ? "NEW ENTRIES STOPPED"
                : risk.recon_state && risk.recon_state !== "RECONCILED" ? `RECONCILIATION ${risk.recon_state}` : "Clear to trade"}
          </div>
          <div className="mt-1 text-[11px] text-slate-500">{blocked ? "New entries are blocked; exits keep running." : "Reconciled, no kill switch, entries allowed."}</div>
        </Panel>

        <Panel title="Open now" icon={Activity} testid="dash-open">
          <Row label="Open trades" value={t.open_trades ?? 0} testid="dash-open-trades" />
          <div className="flex gap-2 text-[11px] mono text-slate-400" data-testid="dash-open-by-env">
            {byEnv.LIVE > 0 && <span className="text-[#FF6b62] font-bold">LIVE {byEnv.LIVE}</span>}
            {byEnv.SHADOW + byEnv.PAPER > 0 && <span title={`shadow ${byEnv.SHADOW} · paper ${byEnv.PAPER}`}>SIMULATED {byEnv.SHADOW + byEnv.PAPER}</span>}
            {!rows.length && <span>nothing open</span>}
          </div>
          <Row label="Unrealised P&L" value={unpriced ? "—" : inrPnl(t.unrealised_pnl, 0)} cls={pnlColor(t.unrealised_pnl)} testid="dash-unrealised" />
          {!unpriced && t.unpriced_trades > 0 && (
            <div className="text-[10px] font-bold text-[#FF9500]" data-testid="dash-unpriced"
              title="Trades without a current price are not in the total: it is a partial figure">
              ⚠ {t.open_trades - t.unpriced_trades} of {t.open_trades} trades priced · {t.unpriced_trades} not in the total
            </div>)}
        </Panel>

        <Panel title="Today" icon={Wallet} testid="dash-today">
          <Row label="Realised P&L" value={inrPnl(s.realised_pnl ?? 0, 0)} cls={pnlColor(s.realised_pnl)} testid="dash-realised-today" />
          <Row label="Completed trades" value={s.completed_trades ?? 0} />
          {products.futures && status.market_status && (
            <div className={`text-[11px] mt-1 ${marketLive ? "text-[#00C805]" : "text-slate-500"}`}>Futures market data {status.market_status}
              {parity && parity.passed === false ? " · parity FAILED" : ""}</div>
          )}
        </Panel>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="dash-actions">
        {products.futures && <Go to="/opportunities" icon={Crosshair} label="Futures Opportunities" testid="dash-view-opportunities" />}
        {products.futures && <Go to="/paper" icon={Zap} label="Futures Trade Panel" testid="dash-open-paper" />}
        {products.options && <Go to="/options/scanner" icon={SlidersHorizontal} label="Options Scanner" testid="dash-open-scanner" />}
        {products.options && <Go to="/options/hedge" icon={Layers} label="Options Hedging" testid="dash-open-hedge" />}
        <Go to="/active-trades" icon={Activity} label="Active Trades" primary testid="dash-active-trades" />
        <Go to="/book?tab=positions" icon={BookOpen} label="Positions & P&L" testid="dash-book" />
      </div>
    </div>
  );
}
